import { SellerAccolade } from '@c/seller/Accolades';
import { useQuery } from '@tanstack/react-query';
import { getProductsByIds } from '@util/firestore/products';
import { StarIcon } from 'lucide-react';
import { ReviewDocument } from 'models/review';
import { PublicUserDocument } from 'models/user';
import Link from 'next/link';
import ReviewModal from './modals/ReviewModal';
import { useState, useEffect } from 'react';
import MobileReviewModal from './modals/MobileReviewModal';
import { isMobile } from '@util/index';
import { getImageReviewsByUid, getReviewsByUid } from '@util/firestore/reviews';
import { PaginatorClient } from '@c/ui/Paginator';
import SafeImage from '@ui/SafeImage';

type ReviewsTabProps = {
  user: PublicUserDocument;
  reviews: ReviewDocument[];
};

export default function ReviewsTab({
  user,
  reviews: initialReviews,
}: ReviewsTabProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const reviewsPerPage = 40;
  const totalPages = Math.ceil(user.reviews / reviewsPerPage);

  const { data: reviews = initialReviews } = useQuery({
    queryKey: ['reviews', user.uid, currentPage],
    queryFn: () =>
      getReviewsByUid({ uid: user.uid, reviewsPerPage, page: currentPage }),
    initialData: initialReviews,
  });

  const { data: imageReviews = [] } = useQuery({
    queryKey: ['reviews', user.uid],
    queryFn: () => getImageReviewsByUid({ uid: user.uid }),
    throwOnError: true,
  });

  const allProductIds = [
    ...reviews.map((r) => r.products.map((p) => p.id)).flat(),
    ...imageReviews.map((r) => r.products.map((p) => p.id)).flat(),
  ];
  const uniqueProductIds = [...new Set(allProductIds)];

  const { data: products } = useQuery({
    queryKey: ['products', uniqueProductIds],
    queryFn: () => getProductsByIds(uniqueProductIds),
    enabled: uniqueProductIds.length > 0,
  });

  const reviewsToShowGallery = 3;

  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const selectedReview = imageReviews[selectedIndex ?? 0];

  const showArrows = imageReviews.length > 1;

  const handlePrev = () => {
    if (selectedIndex === null) return;
    setSelectedIndex(
      (selectedIndex + imageReviews.length - 1) % imageReviews.length
    );
  };

  const handleNext = () => {
    if (selectedIndex === null) return;
    setSelectedIndex((selectedIndex + 1) % imageReviews.length);
  };

  // Keyboard controls for modal navigation
  useEffect(() => {
    if (selectedIndex === null) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        if (showArrows && handlePrev) handlePrev();
      } else if (e.key === 'ArrowRight') {
        if (showArrows && handleNext) handleNext();
      } else if (e.key === 'Escape') {
        setSelectedIndex(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedIndex, showArrows]);

  if (!reviews.length) {
    return (
      <div className="flex h-[30rem] w-full items-center justify-center">
        <h3 className="text-[2.1rem] font-medium text-zinc-600">
          This seller has no reviews yet.
        </h3>
      </div>
    );
  }

  return (
    <div className="mt-8 flex w-full flex-col gap-[3rem] p-4 sm:py-5">
      <div className="flex w-full justify-between">
        {imageReviews.length >= reviewsToShowGallery && (
          <h3 className="w-full text-[2.5rem] font-bold text-black sm:text-[3rem]">
            Photos from reviews
          </h3>
        )}

        <div className="hidden w-full items-center justify-end gap-x-2 sm:flex">
          <div className="flex items-center gap-x-4">
            <StarIcon className="h-[3rem] w-[3rem] fill-brand-secondary text-brand-secondary" />

            <span className="pt-2 text-[2.5rem] font-bold -tracking-[0.1rem] text-black">
              {user.rating.toFixed(1)}
            </span>

            <div className="h-2 w-2 rounded-full bg-black/30" />

            <span className="pt-1 text-[1.8rem] font-medium text-zinc-600">
              {user.reviews} Reviews
            </span>
          </div>
        </div>
      </div>

      <div className="flex w-full items-center gap-x-8 overflow-x-scroll">
        {imageReviews.length >= reviewsToShowGallery &&
          imageReviews.map((review, idx) => (
            <button
              type="button"
              onClick={() => setSelectedIndex(idx)}
              key={review.id}
              aria-label={`View ${review.comment}`}
              className="min-w-[12rem] shrink-0 sm:min-w-[40rem]"
            >
              <SafeImage
                src={review.images?.[0]?.thumb ?? ''}
                alt={review.comment}
                width={3200}
                height={3200}
                className="h-[12rem] w-[12rem] shrink-0 cursor-pointer rounded-[1.5rem] object-cover sm:h-[40rem] sm:w-[40rem] sm:rounded-[3rem]"
              />
            </button>
          ))}
      </div>

      {selectedReview && (
        <ReviewModal
          review={selectedReview}
          product={products?.find((p) =>
            selectedReview?.products.find((rp) => rp.id === p.id)
          )}
          open={selectedIndex !== null && !isMobile()}
          onClose={() => setSelectedIndex(null)}
          onPrev={showArrows ? handlePrev : undefined}
          onNext={showArrows ? handleNext : undefined}
        />
      )}

      {selectedReview && (
        <MobileReviewModal
          review={selectedReview}
          open={selectedIndex !== null && isMobile()}
          dismiss={() => setSelectedIndex(null)}
          onPrev={showArrows ? handlePrev : undefined}
          onNext={showArrows ? handleNext : undefined}
        />
      )}

      {reviews.map((review) => (
        <FullReviewCard key={review.id} review={review} />
      ))}

      <PaginatorClient
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />
    </div>
  );
}

const FullReviewCard = ({ review }: { review: ReviewDocument }) => {
  const reviewDate = new Date(review.created).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const validProducts = review.products.filter((p) => p.id);

  const { data: products } = useQuery({
    queryKey: ['products', validProducts.map((p) => p.id)],
    queryFn: () => getProductsByIds(validProducts.map((p) => p.id)),
    enabled: !!validProducts.length,
  });

  return (
    <div className="flex w-full border-b border-zinc-200 pb-8">
      <div className="flex w-full flex-col items-start justify-between sm:flex-row sm:items-center">
        <div className="flex flex-col gap-y-8 sm:gap-y-16">
          <div className="flex flex-col gap-y-8">
            <div className="flex items-center gap-x-8">
              <SafeImage
                src={review.reviewer_photo}
                alt={review.reviewer_username}
                width={80}
                height={80}
                className="h-[5rem] w-[5rem] rounded-full object-cover shadow-md"
              />

              <span className="text-[2.1rem] font-semibold text-black underline">
                {review.reviewer_username}
              </span>

              <span className="text-[1.6rem] font-medium text-brand-gray sm:text-[1.8rem]">
                {reviewDate}
              </span>
            </div>

            <div className="flex flex-col items-start gap-8 sm:flex-row sm:items-center">
              <div className="flex items-center gap-x-2">
                {[...Array(5)].map((_, i) => (
                  <StarIcon
                    key={i}
                    className={`h-[3rem] w-[3rem] ${
                      i < review.rating
                        ? 'fill-brand-secondary text-transparent'
                        : 'fill-zinc-300 text-transparent'
                    }`}
                  />
                ))}
              </div>

              <div className="flex flex-wrap items-center gap-8 sm:flex-row">
                {review.fast_shipper && (
                  <SellerAccolade accolade="fast_shipper" count={1} />
                )}
                {review.as_described && (
                  <SellerAccolade accolade="accurate_describer" count={1} />
                )}
                {review.quick_replies && (
                  <SellerAccolade accolade="quick_responder" count={1} />
                )}
              </div>
            </div>
          </div>

          {review.comment ? (
            <p className="text-[1.8rem] font-medium text-brand-dark-gray">
              {review.comment}
            </p>
          ) : (
            <p className="text-[1.8rem] font-medium text-brand-gray/60">
              This review has no comment.
            </p>
          )}
        </div>

        {!!products?.length && (
          <>
            {products.length > 1 ? (
              <div className="mt-12 flex flex-row gap-4 sm:flex-col sm:gap-2">
                <div className="flex items-center gap-x-2">
                  {products.map((p) => (
                    <Link key={p.id} href={`/product/${p.slug}`}>
                      <SafeImage
                        src={p.thumbnail}
                        alt={p.title}
                        width={3200}
                        height={3200}
                        className="h-[7rem] w-[7rem] object-cover sm:h-[10rem] sm:w-[10rem]"
                      />
                    </Link>
                  ))}
                </div>

                <div className="flex flex-col gap-2">
                  <h3 className="w-[30rem] text-[1.8rem] font-semibold text-black sm:text-[2.1rem]">
                    Multiple products
                  </h3>

                  <span className="w-[20rem] text-[1.6rem] font-medium text-brand-gray sm:w-full sm:text-[1.8rem]">
                    This review includes {products.length} products
                  </span>
                </div>
              </div>
            ) : (
              <Link
                href={`/product/${products[0].slug}`}
                target="_blank"
                className="mt-12 flex flex-row gap-4 sm:flex-col sm:gap-2"
                key={products[0].id}
              >
                <SafeImage
                  src={products[0].thumbnail}
                  alt={products[0].title}
                  width={3200}
                  height={3200}
                  className="h-[7rem] w-[7rem] object-contain sm:h-[20rem] sm:w-[20rem]"
                />

                <div className="flex flex-col gap-2">
                  <h3 className="line-clamp-1 w-[30rem] text-[1.8rem] font-semibold text-black sm:line-clamp-2 sm:text-[2.1rem]">
                    {products[0].title}
                  </h3>

                  <span className="text-[1.6rem] font-medium text-brand-gray sm:text-[1.8rem]">
                    {products[0].category}
                  </span>
                </div>
              </Link>
            )}
          </>
        )}
      </div>
    </div>
  );
};
